import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Send, Mail, MessageSquare, User, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 relative" data-testid="contact-section">
      {/* Background glow */}
      <div className="absolute top-0 left-1/4 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-12"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Contact
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Get In
            <span className="text-gradient"> Touch</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto px-4">
            Have questions about 2A Valuence? We'd love to hear from you. 
            Send us a message and we'll respond as soon as possible.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-4 sm:p-6 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="text-slate-300 text-xs sm:text-sm mb-2 flex items-center gap-2">
                  <User className="w-3 h-3 sm:w-4 sm:h-4" />
                  Your Name
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="bg-white/5 border-white/10 focus:border-primary h-11 sm:h-12 text-white placeholder:text-slate-500 text-sm sm:text-base"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label className="text-slate-300 text-xs sm:text-sm mb-2 flex items-center gap-2">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="bg-white/5 border-white/10 focus:border-primary h-11 sm:h-12 text-white placeholder:text-slate-500 text-sm sm:text-base"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-300 text-xs sm:text-sm mb-2 flex items-center gap-2">
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                Subject
              </label>
              <Input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="How can we help?"
                className="bg-white/5 border-white/10 focus:border-primary h-11 sm:h-12 text-white placeholder:text-slate-500 text-sm sm:text-base"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label className="text-slate-300 text-xs sm:text-sm mb-2 block">Message</label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Tell us more about your inquiry..."
                rows={4}
                className="bg-white/5 border-white/10 focus:border-primary text-white placeholder:text-slate-500 resize-none text-sm sm:text-base"
                data-testid="contact-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 py-5 sm:py-6"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  Send Message
                </>
              )}
            </Button>

            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-[#10B981] bg-[#10B981]/10 p-3 sm:p-4 rounded-lg text-sm sm:text-base"
                data-testid="contact-success-message"
              >
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                Message sent successfully! We'll get back to you soon.
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 bg-red-500/10 p-3 sm:p-4 rounded-lg text-sm sm:text-base"
                data-testid="contact-error-message"
              >
                Something went wrong. Please try again or email us directly at 2avaluence@gmail.com
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
